import { ColumnConfig } from "./config/ColumnConfig";
import { ColumnMatcher, ColumnMatcherData } from "./ColumnMatcher";
import { MatcherFactory } from "./matchers/factory/MatcherFactory";
import { Matcher, MatcherProperties } from "./matchers/Matcher";
import { RegexMatcher } from "./matchers/RegexMatcher";
import { TableConfig } from "./config/TableConfig";
import { TransformRuleFactory } from "./transformation/factory/TransformRuleFactory";
import {
  TransformRule,
  TransformRuleData,
} from "./transformation/TransformRule";
import {
  setupRulesets,
  ValidationRuleSet,
} from "./validation/ValidationRuleSet";
import { ValidationRule } from "./validation/ValidationRule";

export { TableModel, TableModelData };

type TableModelData<T extends MatcherProperties = MatcherProperties> = {
  name: string;
  columns: ColumnMatcherData[];

  startOffset: number;
  startOffsetEnd: number;
  handleOffset: boolean;

  allowEmpty: boolean;
  generateReuse: boolean;

  startPattern: T;
  softStopPattern: T;
  // TODO: rename to HardStop
  stopPattern: T;
  ignorePatterns: T[];

  validators: ValidationRuleSet[];

  transformationRules: TransformRuleData[];
};

class TableModel {
  name: string;

  startOffset: number;
  startOffsetEnd: number;
  handleOffset: boolean;

  allowEmpty: boolean;
  generateReuse: boolean;

  startPattern: Matcher;
  softStopPattern: Matcher;
  // TODO: rename to HardStop
  stopPattern: Matcher;
  columns: ColumnMatcher[];
  ignorePatterns: Matcher[];

  validators: ValidationRuleSet[];

  transformationRules: TransformRule[];

  constructor(data: TableModelData) {
    this.name = data.name;

    this.allowEmpty = data.allowEmpty;
    this.generateReuse = data.generateReuse;

    this.startOffset = data.startOffset;
    this.startOffsetEnd = data.startOffsetEnd;
    this.handleOffset = data.handleOffset;

    // setup Start and Soft/Hard Stop patterns
    this.startPattern = MatcherFactory.createMatcher(data.startPattern);
    this.softStopPattern = MatcherFactory.createMatcher(data.softStopPattern);
    this.stopPattern = MatcherFactory.createMatcher(data.stopPattern);

    this.columns = data.columns
      .sort((a, b) => a.columnName.localeCompare(b.columnName))
      .map((c) => new ColumnMatcher(c));
    this.ignorePatterns = data.ignorePatterns
      .map(MatcherFactory.createMatcher)
      .sort((a, b) => a.position - b.position);

    this.validators = setupRulesets(data);

    this.transformationRules = (data.transformationRules ?? [])
      .sort((a, b) => a.position - b.position)
      .map(TransformRuleFactory.createRule);
  }

  /**
   * Change value of generateReuse, return new value
   */
  toggleReuseable(): boolean {
    this.generateReuse = !this.generateReuse;
    return this.generateReuse;
  }
  getModelId(): string {
    let id =
      `TABLE-[${this.startPattern.getMatcherId()}]-` +
      `[${this.softStopPattern.getMatcherId()}]-` +
      `[${this.stopPattern.getMatcherId()}]-`;
    id += `[${this.ignorePatterns.map((ip) => ip.getMatcherId() + "-")}]`;
    id += `[${this.columns.map((col) => col.getMatcherId() + "-")}]`;
    // id += `[${this.transformationRules.map(x => x.getRuleId() + '-')}]`;
    id += `[${this.startOffset}-${this.startOffsetEnd}-${this.handleOffset}-${this.allowEmpty}]`;

    return id;
  }

  isConfigured(): boolean {
    return this.startPattern.isConfigured();
  }

  static createFromConfig(tableConfig: TableConfig): TableModel {
    const columnDatas: ColumnMatcherData[] = tableConfig.columnNames.map((x) =>
      ColumnMatcher.createFromConfig(x),
    );
    if (columnDatas.length > 0) columnDatas[0].anchor = true;
    const tableModelData: TableModelData = {
      name: tableConfig.name,
      generateReuse: true,
      allowEmpty: false,
      startOffset: 0,
      startOffsetEnd: 0,
      handleOffset: false,
      startPattern: RegexMatcher.createTest(),
      softStopPattern: RegexMatcher.createTest(),
      stopPattern: RegexMatcher.createTest(),
      columns: columnDatas,
      ignorePatterns: [],
      transformationRules: [],
      validators: tableConfig.columnNames.map((x) => ({
        clientValidationOperator: "OR",
        validationRules: [],
        fieldName: x.fieldName,
      })),
    };
    return new TableModel(tableModelData);
  }

  updateStartPattern(matcher: Matcher) {
    this.startPattern = matcher;
  }

  updateSoftStopPattern(matcher: Matcher) {
    this.softStopPattern = matcher;
  }

  updateHardStopPattern(matcher: Matcher) {
    this.stopPattern = matcher;
  }

  /**
   * Adds a new IgnoreRule Matcher. If no Matcher is provided, a RegexMatcher will be created.
   * @param matcher
   */
  addIgnoreRule(matcher?: Matcher) {
    this.ignorePatterns.push(matcher ?? RegexMatcher.createTest());
    this.ignorePatterns[this.ignorePatterns.length - 1].position =
      this.ignorePatterns.length;
  }

  /**
   * Remove an ignoreRule by value
   * @param matcher
   * @param oldId
   */
  removeIgnoreRule(matcher: Matcher) {
    // TODO: does this even work?
    const index = this.ignorePatterns.findIndex(
      (ip) => ip.getMatcherId() === matcher.getMatcherId(),
    );
    if (index === -1) return;
    this.ignorePatterns.splice(index, 1);
    this.updateIgnoreRulePositions(index);
  }

  /**
   * Insert a new Ignore Rule at the location of an existing Matcher, otherwise add a new Matcher.
   * @param matcher
   * @param oldId
   */
  replaceIgnoreRule(matcher: Matcher, index: number) {
    if (index === -1) return this.addIgnoreRule(matcher);
    this.ignorePatterns.splice(index, 1, matcher);
    this.updateIgnoreRulePositions(index);
  }

  private updateIgnoreRulePositions(index = 0) {
    for (let i = index; i < this.ignorePatterns.length; i++) {
      this.ignorePatterns[i].position = i;
    }
  }

  getColumn(column: string | ColumnConfig): ColumnMatcher | undefined {
    let columnName: string;
    if (typeof column === "string") {
      columnName = column;
    } else {
      columnName = column.fieldName;
    }
    return this.columns.filter((x) => x.columnName === columnName)[0];
  }

  updateColumn(columnModel: ColumnMatcher) {
    let index = this.columns.findIndex(
      (val: ColumnMatcher) => val.columnName === columnModel.columnName,
    );
    let deleteCount = 1;
    if (index === -1) {
      deleteCount = 0;
      index = 0;
    }
    this.columns.splice(index, deleteCount, columnModel);
  }

  /**
   * Called when no anchor column set. Automatically sets Anchor column to something sensible.
   * Sets all columns to anchor=false if no anchor could be found.
   */
  anchorGone(): void {
    if (this.columns.length === 0) return;
    // if all default value matchers, no anchor column
    if (
      this.columns
        .flatMap((x) => x.matcher.getDiscriminator())
        .filter((x) => x !== "F_DEFAULT").length === 0
    )
      return;
    const possibleAnchors = this.columns.filter(
      (x) => x.matcher.getDiscriminator() !== "F_DEFAULT",
    );
    const newAnchor = possibleAnchors[0];
    if (newAnchor) {
      this.changeAnchor(newAnchor.columnName);
    } else {
      this.columns.forEach((c) => (c.anchor = false));
    }
  }

  changeAnchor(column: string | ColumnConfig) {
    let columnName: string;
    if (typeof column === "string") {
      columnName = column;
    } else {
      columnName = column.fieldName;
    }
    const columnModel = this.columns.find((x) => x.columnName === columnName);
    if (!columnModel) return;

    this.columns.forEach((x) => {
      x.anchor = false;
      x.anchorReverse = false;
    });

    columnModel.anchor = true;
  }

  changeAnchorDirection(column: string | ColumnConfig, reversed: boolean) {
    let columnName: string;
    if (typeof column === "string") {
      columnName = column;
    } else {
      columnName = column.fieldName;
    }
    const columnModel = this.columns.find((x) => x.columnName === columnName);
    if (!columnModel) return;

    columnModel.anchorReverse = reversed;
  }

  getTransformRules(fieldName: string): TransformRule[] {
    return this.transformationRules.filter((x) => x.fieldName === fieldName);
  }
  /**
   *
   * @param fieldName
   * @param transformRules
   * @returns  All TransformRules
   */
  setTransformRules(
    fieldName: string,
    transformRules: TransformRule[],
  ): TransformRule[] {
    this.transformationRules = [
      ...transformRules,
      ...this.transformationRules.filter((x) => x.fieldName !== fieldName),
    ];
    return this.transformationRules;
  }

  getValidationRuleSet(fieldName: string): ValidationRuleSet {
    return (
      this.validators.find((x) => x.fieldName === fieldName) ?? {
        clientValidationOperator: "OR",
        fieldName,
        validationRules: [],
      }
    );
  }
  /**
   *
   * @param fieldName
   * @param ruleset
   * @returns All ValidationRuleSets
   */
  setValidationRuleSet(
    fieldName: string,
    ruleset: ValidationRuleSet,
  ): ValidationRuleSet[] {
    this.validators = [
      ruleset,
      ...this.validators.filter((x) => x.fieldName !== fieldName),
    ];
    return this.validators;
  }
  toTableModelData(): TableModelData {
    return {
      name: this.name,
      startOffset: this.startOffset,
      startOffsetEnd: this.startOffsetEnd,
      handleOffset: this.handleOffset,
      allowEmpty: this.allowEmpty,
      generateReuse: this.generateReuse,
      columns: this.columns.map((c) => c.toMatcherData()),
      ignorePatterns: this.ignorePatterns.map((x) => x.toMatcherData()),
      startPattern: this.startPattern.toMatcherData(),
      softStopPattern: this.softStopPattern.toMatcherData(),
      stopPattern: this.stopPattern.toMatcherData(),
      transformationRules: this.transformationRules.map((x) => x.toData()),
      validators: this.validators,
    };
  }

  static createTest(rest: Partial<TableModelData> = {}): TableModel {
    const defaultTableModel: TableModelData = {
      name: "",
      allowEmpty: false,
      generateReuse: false,
      startOffset: 0,
      startOffsetEnd: 0,
      handleOffset: false,
      columns: [],
      ignorePatterns: [],
      startPattern: RegexMatcher.createTest(),
      softStopPattern: RegexMatcher.createTest(),
      stopPattern: RegexMatcher.createTest(),
      transformationRules: [],
      validators: [],
    };

    return new TableModel({
      ...defaultTableModel,
      ...rest,
    });
  }
}
